import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useColumnClassifications } from '../columnClassificationStore';
import { logDataModels } from '../dataModelsLogger';
import { buildPolicyCatalog } from '../governance/catalog';
import { applicablePolicies, computeImpact, policyAppliesToListing, resolvePolicyTargets, simulatePolicy, summarizeAppliesTo, validatePolicyDraft } from '../governance/engine';
import type {
  GovernancePolicy,
  ImpactPreview,
  PolicyAsset,
  PolicyModel,
  PolicyScope,
  PolicyVault,
  PolicyType,
  SimulationRequest,
  SimulationResult,
} from '../governance/types';
import { POLICY_TYPE_KIND } from '../governance/types';
import { definitionMode, policyDocumentPath, toPolicyDocument } from '../governance/policyInspect';
import type { DataModel, ModelTable, Space } from '../dataModelsTypes';
import { createPolicy, deletePolicy, listPolicies, recordFromApi, updatePolicy as updatePolicyApi, writeFromDetail } from '../policyApi';

export type GovernanceLevel = 'vault' | 'model' | 'asset';
export type PolicyKind = 'Row Filter' | 'Column Mask' | 'Data Access' | 'Classification' | 'Time Access';
export type PolicyStatus = 'Enabled' | 'Disabled' | 'Draft';

export interface GovernancePolicyRecord {
  id: string;
  level: GovernanceLevel;
  ownerId: string;
  name: string;
  description: string;
  kind: PolicyKind;
  flag: 'mandatory' | 'standard' | 'override';
  overridesId?: string;
  appliesTo: string;
  status: PolicyStatus;
  updated: string;
  actor: string;
  library?: boolean;
  persisted?: boolean;
  policyServerSynced?: boolean;
  detail?: GovernancePolicy;
}

export interface ResolvedPolicy extends GovernancePolicyRecord {
  ownedHere: boolean;
}

interface GovernanceContextValue {
  policies: GovernancePolicyRecord[];
  assets: PolicyAsset[];
  policyModels: PolicyModel[];
  vaults: PolicyVault[];
  addPolicy: (policy: GovernancePolicyRecord) => Promise<GovernancePolicyRecord | null>;
  updatePolicy: (id: string, patch: Partial<GovernancePolicyRecord>) => Promise<GovernancePolicyRecord | null>;
  removePolicy: (id: string) => Promise<boolean>;
  createPolicyDraft: (policy: GovernancePolicy) => Promise<GovernancePolicyRecord>;
  updatePolicyDraft: (policy: GovernancePolicy) => Promise<GovernancePolicyRecord | null>;
  validatePolicy: (policy: GovernancePolicy) => string[];
  activatePolicy: (policy: GovernancePolicy) => Promise<GovernancePolicyRecord>;
  deactivatePolicy: (policy: GovernancePolicy) => Promise<GovernancePolicyRecord>;
  isPolicyKeyTaken: (policy: GovernancePolicy) => boolean;
  getApplicablePolicies: (scope: PolicyScope) => GovernancePolicy[];
  resolvePolicyTargets: (policy: GovernancePolicy, scope: PolicyScope) => PolicyAsset[];
  simulatePolicy: (policy: GovernancePolicy, request: SimulationRequest) => SimulationResult;
  previewImpact: (policy: GovernancePolicy) => ImpactPreview;
}

const GovernanceContext = createContext<GovernanceContextValue | null>(null);

export function recordFromDetail(detail: GovernancePolicy, flag: GovernancePolicyRecord['flag'], status: PolicyStatus, actor: string, appliesTo = ''): GovernancePolicyRecord {
  const level: GovernanceLevel = detail.scope.level === 'VAULT' ? 'vault' : detail.scope.level === 'MODEL' ? 'model' : 'asset';
  const ownerId = detail.scope.level === 'VAULT' ? detail.scope.vaultId : detail.scope.level === 'MODEL' ? detail.scope.modelId ?? '' : detail.scope.assetId ?? '';
  return {
    id: detail.id,
    level,
    ownerId,
    name: detail.name,
    description: detail.description || 'Policy',
    kind: POLICY_TYPE_KIND[detail.type],
    flag,
    appliesTo,
    status,
    updated: status === 'Draft' ? 'Just now' : 'Today',
    actor,
    persisted: actor === 'You',
    detail,
  };
}

export function resolveGovernance(
  policies: GovernancePolicyRecord[],
  scope: { level: GovernanceLevel; spaceId: string; modelId?: string; tableId?: string; inherit: boolean },
  assets: PolicyAsset[] = [],
): ResolvedPolicy[] {
  const ownId = scope.level === 'vault' ? scope.spaceId : scope.level === 'model' ? scope.modelId : scope.tableId;
  const own = policies.filter((policy) => policy.level === scope.level && policy.ownerId === ownId);
  const parents = policies.filter((policy) => {
    if (scope.level === 'model') return policy.level === 'vault' && policy.ownerId === scope.spaceId;
    if (scope.level === 'asset') {
      return (policy.level === 'vault' && policy.ownerId === scope.spaceId) || (policy.level === 'model' && policy.ownerId === scope.modelId);
    }
    return false;
  });
  const overridden = new Set(own.map((policy) => policy.overridesId).filter((id): id is string => Boolean(id)));
  const listingScope = toScope(scope.level, scope.spaceId, scope.modelId, scope.tableId);
  const candidates = parents
    .filter((policy) => !overridden.has(policy.id))
    .filter((policy) => scope.inherit || policy.flag === 'mandatory');
  const inherited = candidates
    .filter((policy) => {
      if (!policy.detail || !assets.length) return true;
      return policyAppliesToListing(policy.detail, listingScope, assets);
    })
    .map((policy) => ({ ...policy, ownedHere: false }));
  if (candidates.length !== inherited.length) {
    logDataModels('app', 'governance_inherited_filtered', {
      level: scope.level,
      parents: candidates.length,
      kept: inherited.length,
      spaceId: scope.spaceId,
      modelId: scope.modelId || '',
      tableId: scope.tableId || '',
    });
  }
  return [...inherited, ...own.map((policy) => ({ ...policy, ownedHere: true }))];
}

function toScope(level: GovernanceLevel, spaceId: string, modelId?: string, tableId?: string): PolicyScope {
  if (level === 'model') return { level: 'MODEL', vaultId: spaceId, modelId };
  if (level === 'asset') return { level: 'ASSET', vaultId: spaceId, modelId, assetId: tableId };
  return { level: 'VAULT', vaultId: spaceId };
}

export function launchScope(level: GovernanceLevel, spaceId: string, modelId?: string, tableId?: string): PolicyScope {
  return toScope(level, spaceId, modelId, tableId);
}

export function GovernanceProvider({
  spaces,
  models,
  tables = [],
  children,
}: {
  spaces: Space[] | Array<{ id: string }>;
  models: DataModel[] | Array<{ id: string }>;
  tables?: ModelTable[];
  children: React.ReactNode;
}) {
  const { overrides } = useColumnClassifications();
  const catalog = useMemo(
    () => buildPolicyCatalog(spaces as Space[], models as DataModel[], tables, overrides),
    [spaces, models, tables, overrides],
  );
  const [policies, setPolicies] = useState<GovernancePolicyRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await listPolicies();
        if (cancelled) return;
        setPolicies(rows.map((row) => recordFromApi(row)));
        logDataModels('app', 'governance_store_initialized', { persisted: rows.length });
      } catch (error) {
        if (cancelled) return;
        logDataModels('error', 'governance_store_load_failed', { message: error instanceof Error ? error.message : 'failed' });
        setPolicies([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(
    () => policies.map((policy) => (
      policy.detail
        ? { ...policy, appliesTo: summarizeAppliesTo(policy.detail, catalog.assets) }
        : policy
    )),
    [catalog.assets, policies],
  );

  const details = (): GovernancePolicy[] => visible.map((row) => row.detail).filter((item): item is GovernancePolicy => Boolean(item));

  const persistDetail = useCallback(async (policy: GovernancePolicy, status: PolicyStatus, create: boolean): Promise<GovernancePolicyRecord> => {
    const next = withPolicyIdentity(policy);
    const complexity = definitionMode(policy.document);
    const document = toPolicyDocument({ ...next, id: create ? '' : next.id }, {
      path: policyDocumentPath(next),
      assets: catalog.assets,
      complexity,
    });
    const body = writeFromDetail({ ...next, id: create ? '' : next.id, document }, status, 'standard');
    const saved = create ? await createPolicy(body) : await updatePolicyApi(next.id, body);
    const record = recordFromApi(saved, catalog.assets);
    logDataModels('app', 'governance_policy_document_persisted', {
      id: record.id,
      create,
      schema: document.schema,
      mode: complexity,
    });
    setPolicies((current) => [record, ...current.filter((row) => row.id !== record.id)]);
    return record;
  }, [catalog.assets]);

  const value = useMemo<GovernanceContextValue>(() => ({
    policies: visible,
    assets: catalog.assets,
    policyModels: catalog.models,
    vaults: catalog.vaults,
    addPolicy: async (policy) => {
      if (!policy.detail) {
        logDataModels('warning', 'governance_policy_create_skipped', { id: policy.id, reason: 'missing_detail' });
        return null;
      }
      const record = await persistDetail(policy.detail, policy.status, true);
      logDataModels('app', 'governance_policy_created', { id: record.id, level: record.level, ownerId: record.ownerId, name: record.name });
      return record;
    },
    updatePolicy: async (id, patch) => {
      const current = visible.find((policy) => policy.id === id);
      if (!current?.detail) {
        logDataModels('warning', 'governance_policy_update_skipped', { id, reason: 'missing_detail' });
        return null;
      }
      const status = patch.status ?? current.status;
      const next = { ...current.detail, name: patch.name ?? current.detail.name, description: patch.description ?? current.detail.description };
      const record = await persistDetail(next, status, false);
      logDataModels('app', 'governance_policy_updated', { id, ...patch });
      return record;
    },
    removePolicy: async (id) => {
      const synced = await deletePolicy(id);
      setPolicies((current) => current.filter((policy) => policy.id !== id));
      logDataModels('app', 'governance_policy_removed', { id, opaSynced: synced });
      return synced;
    },
    createPolicyDraft: async (policy) => {
      const next = withPolicyIdentity({ ...policy, id: '' });
      const record = await persistDetail({ ...next, status: 'DRAFT' }, 'Draft', true);
      logDataModels('app', 'governance_policy_draft_saved', { id: record.id, type: policy.type, policyKey: next.policyKey });
      return record;
    },
    updatePolicyDraft: async (policy) => {
      if (!policy.id && !policy.policyKey) return null;
      const next = withPolicyIdentity(policy);
      const record = await persistDetail(next, listStatusFromDetail(next.status), false);
      logDataModels('app', 'governance_policy_draft_updated', { id: record.id, status: next.status });
      return record;
    },
    validatePolicy: (policy) => validatePolicyDraft(policy, catalog.assets),
    activatePolicy: async (policy) => {
      const next = withPolicyIdentity({ ...policy, status: 'ACTIVE' });
      const record = await persistDetail(next, 'Enabled', !policy.id);
      logDataModels('app', 'governance_policy_activated', { id: record.id, type: policy.type });
      return record;
    },
    deactivatePolicy: async (policy) => {
      const next = withPolicyIdentity({ ...policy, status: 'INACTIVE' });
      const record = await persistDetail(next, 'Disabled', !policy.id);
      logDataModels('app', 'governance_policy_deactivated', { id: record.id, type: policy.type });
      return record;
    },
    isPolicyKeyTaken: (policy) => {
      const next = withPolicyIdentity(policy);
      const owner = next.scope.assetId || next.scope.modelId || next.scope.vaultId;
      return visible.some((row) => {
        if (policy.id && row.id === policy.id) return false;
        const key = row.detail?.policyKey || '';
        return Boolean(key) && key === next.policyKey && row.ownerId === owner;
      });
    },
    getApplicablePolicies: (scope) => applicablePolicies(details(), scope, catalog.assets),
    resolvePolicyTargets: (policy, scope) => resolvePolicyTargets({ ...policy, scope }, catalog.assets),
    simulatePolicy: (policy, request) => {
      const inherited = applicablePolicies(details(), policy.scope, catalog.assets).filter((item) => item.id !== policy.id && item.status === 'ACTIVE');
      return simulatePolicy(policy, inherited, catalog.assets, request);
    },
    previewImpact: (policy) => {
      const inherited = applicablePolicies(details(), policy.scope, catalog.assets).filter((item) => item.id !== policy.id && item.status === 'ACTIVE');
      return computeImpact(policy, catalog.assets, catalog.models, inherited);
    },
  }), [catalog.assets, catalog.models, catalog.vaults, persistDetail, visible]);

  return <GovernanceContext.Provider value={value}>{children}</GovernanceContext.Provider>;
}

export function useGovernance(): GovernanceContextValue {
  const value = useContext(GovernanceContext);
  if (!value) throw new Error('Governance is only available inside WorkSpace.');
  return value;
}

export function kindFromType(type: PolicyType): PolicyKind {
  return POLICY_TYPE_KIND[type];
}

export function listStatusFromDetail(status: GovernancePolicy['status']): PolicyStatus {
  if (status === 'ACTIVE') return 'Enabled';
  if (status === 'INACTIVE') return 'Disabled';
  return 'Draft';
}

export function detailStatusFromList(status: PolicyStatus): GovernancePolicy['status'] {
  if (status === 'Enabled') return 'ACTIVE';
  if (status === 'Disabled') return 'INACTIVE';
  return 'DRAFT';
}

function withPolicyIdentity(policy: GovernancePolicy): GovernancePolicy {
  const policyKey = policy.policyKey?.trim() || slugFromName(policy.name);
  const owner = policy.scope.assetId || policy.scope.modelId || policy.scope.vaultId;
  const id = policy.id || `${policy.scope.level.toLowerCase()}:${owner}:${policyKey}`;
  return { ...policy, id, policyKey, generateId: Boolean(policy.generateId) };
}

function slugFromName(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64) || `policy-${Date.now().toString(36)}`;
}
